"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function InquiryBookingForm() {
  return <section id="inquiry-booking-form" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-dark-section text-dark-foreground py-20 px-4">
      {}
      <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1549040/img/jungle_canopy_twilight_backdrop.png)] opacity-40" style={{
      transform: "translateZ(0)",
      willChange: "transform"
    }} />

      {}
      <div className="absolute inset-0 bg-gradient-to-b from-accent-dark/80 via-accent/60 to-dark-section/90" />

      {}
      <div className="relative z-10 w-full max-w-2xl">
        <FormProvider className="space-y-8 animate-reveal" formId="inquiry_booking_form_contact" sectionName="inquiry-booking-form" defaultValues={{
        full_name: "",
        email: "",
        phone: "",
        preferred_date: "",
        guest_count: "",
        tour_interest: "",
        message: ""
      }} validationRules={{
        full_name: {
          required: true,
          minLength: 2,
          maxLength: 100
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        guest_count: {
          required: true,
          min: 1,
          max: 20
        },
        tour_interest: {
          required: true
        }
      }} submitText="Submit Inquiry" successMessage="Thank you for your inquiry! We'll craft a personalized experience for you and be in touch within 24 hours." errorMessage="We couldn't process your request. Please try again or contact us directly.">
          <ContactFormContent />
        </FormProvider>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  if (isSubmitted && !submitError) {
    return <div className="glass-effect p-8 rounded-2xl space-y-6 animate-reveal">
        <div className="text-center space-y-4">
          <div className="size-16 mx-auto bg-primary/20 rounded-full flex items-center justify-center">
            <svg className="size-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="font-display text-2xl text-dark-foreground">{successMessage}</h3>
        </div>
        <Button onClick={resetForm} variant="outline" className="w-full bg-transparent border-primary/30 text-dark-foreground hover:bg-primary/10 hover:border-primary/50 transition-all duration-[var(--duration-medium)]">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <div className="space-y-8">
      {}
      <div ref={titleRef} className={`text-center space-y-4 transition-all duration-[var(--duration-slow)] ease-[var(--ease-spring)] ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <h2 className="font-display text-4xl md:text-5xl text-dark-foreground tracking-tight">
          Inquiry & Booking Form
        </h2>
        <p className="text-muted text-lg max-w-xl mx-auto">
          Share your vision with us and let our team curate an unforgettable luxury eco-experience in the Riviera Maya.
        </p>
      </div>

      {}
      <div ref={formRef} className={`glass-effect p-8 md:p-10 rounded-2xl transition-all duration-[var(--duration-slow)] ease-[var(--ease-spring)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <Form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {}
            <FormField control={control} name="full_name" rules={{
            required: "Full name is required",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters"
            },
            maxLength: {
              value: 100,
              message: "Name must not exceed 100 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-dark-foreground">Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Jane Doe" {...field} className="bg-dark-section/30 border-b-2 border-secondary/40 border-t-0 border-x-0 rounded-none px-0 shadow-none focus-visible:border-secondary focus-visible:ring-0 focus-visible:rounded-lg focus-visible:border focus-visible:px-3 transition-all duration-[var(--duration-medium)] text-dark-foreground placeholder:text-muted" />
                  </FormControl>
                  <FormDescription className="text-muted/80 text-xs">
                    Enter your first and last name.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="email" rules={{
            required: "Email address is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-dark-foreground">Email Address</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="jane@example.com" {...field} className="bg-dark-section/30 border-b-2 border-secondary/40 border-t-0 border-x-0 rounded-none px-0 shadow-none focus-visible:border-secondary focus-visible:ring-0 focus-visible:rounded-lg focus-visible:border focus-visible:px-3 transition-all duration-[var(--duration-medium)] text-dark-foreground placeholder:text-muted" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="phone" rules={{
            required: "Phone number is required",
            pattern: {
              value: /^[\d\s+()-]+$/,
              message: "Please enter a valid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-dark-foreground">Phone Number</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 555-123-4567" {...field} className="bg-dark-section/30 border-b-2 border-secondary/40 border-t-0 border-x-0 rounded-none px-0 shadow-none focus-visible:border-secondary focus-visible:ring-0 focus-visible:rounded-lg focus-visible:border focus-visible:px-3 transition-all duration-[var(--duration-medium)] text-dark-foreground placeholder:text-muted" />
                  </FormControl>
                  <FormDescription className="text-muted/80 text-xs">
                    International format preferred.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <div className="grid md:grid-cols-2 gap-6">
              {}
              <FormField control={control} name="preferred_date" render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-dark-foreground">Preferred Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} className="bg-dark-section/30 border-b-2 border-secondary/40 border-t-0 border-x-0 rounded-none px-0 shadow-none focus-visible:border-secondary focus-visible:ring-0 focus-visible:rounded-lg focus-visible:border focus-visible:px-3 transition-all duration-[var(--duration-medium)] text-dark-foreground" />
                    </FormControl>
                    <FormDescription className="text-muted/80 text-xs">
                      If you have a specific date in mind, select it here.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>} />

              {}
              <FormField control={control} name="guest_count" rules={{
              required: "Number of guests is required",
              min: {
                value: 1,
                message: "At least 1 guest required"
              },
              max: {
                value: 20,
                message: "Maximum 20 guests allowed"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className="text-dark-foreground">Number of Guests</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="2" min="1" max="20" {...field} className="bg-dark-section/30 border-b-2 border-secondary/40 border-t-0 border-x-0 rounded-none px-0 shadow-none focus-visible:border-secondary focus-visible:ring-0 focus-visible:rounded-lg focus-visible:border focus-visible:px-3 transition-all duration-[var(--duration-medium)] text-dark-foreground placeholder:text-muted" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            {}
            <FormField control={control} name="tour_interest" rules={{
            required: "Please select a tour or experience"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-dark-foreground">Tour / Experience of Interest</FormLabel>
                  <FormControl>
                    <select {...field} className="flex h-9 w-full rounded-md border border-secondary/40 bg-dark-section/30 px-3 py-1 text-base text-dark-foreground shadow-xs transition-all outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-secondary/20 disabled:cursor-not-allowed disabled:opacity-50">
                      <option value="" className="bg-dark-section text-dark-foreground">
                        Select an option
                      </option>
                      <option value="Private Yacht Tour" className="bg-dark-section text-dark-foreground">
                        Private Yacht Tour
                      </option>
                      <option value="Jungle Adventure" className="bg-dark-section text-dark-foreground">
                        Jungle Adventure
                      </option>
                      <option value="Cultural Heritage Walk" className="bg-dark-section text-dark-foreground">
                        Cultural Heritage Walk
                      </option>
                      <option value="Custom Luxury Experience" className="bg-dark-section text-dark-foreground">
                        Custom Luxury Experience
                      </option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="message" render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-dark-foreground">Additional Details / Questions</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Tell us more about your preferences, special requests, or any questions you have." {...field} className="bg-dark-section/30 border-secondary/40 min-h-32 focus-visible:border-secondary focus-visible:ring-secondary/20 text-dark-foreground placeholder:text-muted resize-none" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            {submitError && <div className="p-4 bg-secondary/10 border border-secondary/30 rounded-lg">
                <p className="text-secondary text-sm">{submitError}</p>
              </div>}

            {}
            <Button type="submit" disabled={isSubmitting} className="w-full h-12 bg-primary text-foreground-primary font-semibold text-base uppercase tracking-wider rounded-lg shadow-medium hover:scale-[1.03] hover:shadow-strong active:scale-[0.98] transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] relative overflow-hidden group disabled:opacity-60 disabled:hover:scale-100">
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
              <span className="relative z-10 flex items-center justify-center gap-2">
                {isSubmitting ? <>
                    <Loader className="animate-spin size-5" />
                    Submitting...
                  </> : "Submit Inquiry"}
              </span>
            </Button>
          </div>
        </Form>
      </div>
    </div>;
}